import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tutorialService } from '@/services/tutorialService';
import { getRefreshConfig } from '@/config/refresh.config';

export interface TutorialFilters {
  categorySlug?: string;
  categoryId?: string | number;
  [key: string]: unknown;
}

export const useTutorials = (filters: TutorialFilters = {}) => {
  return useQuery({
    queryKey: ['tutorials', filters],
    queryFn: () => {
      if (filters.categorySlug) {
        return tutorialService.getByCategory(filters.categorySlug);
      }
      return tutorialService.list(filters as Record<string, unknown>);
    },
    staleTime: 5 * 60 * 1000,
    ...getRefreshConfig('moderate'),
  });
};

export const useTutorial = (slugOrId: string | number | undefined) => {
  return useQuery({
    queryKey: ['tutorial', slugOrId],
    queryFn: () => {
      const isNumeric = slugOrId != null && !Number.isNaN(Number(slugOrId));
      if (isNumeric) {
        return tutorialService.getById(String(slugOrId));
      }
      return tutorialService.getBySlug(String(slugOrId));
    },
    enabled: !!slugOrId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateTutorial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tutorialData: Record<string, unknown>) => tutorialService.create(tutorialData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tutorials'] });
    },
  });
};

export const useUpdateTutorial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      tutorialService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tutorials'] });
      queryClient.invalidateQueries({ queryKey: ['tutorial', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['tutorial'] });
    },
  });
};

export const useDeleteTutorial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tutorialService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tutorials'] });
    },
  });
};

export const useSearchTutorials = (query: string, options: Record<string, unknown> = {}) => {
  return useQuery({
    queryKey: ['tutorials', 'search', query, options],
    queryFn: () => tutorialService.search(query, options),
    enabled: !!query && query.length > 2,
    staleTime: 2 * 60 * 1000,
  });
};
